import * as React from 'react';
import { Box, Paper, Typography, Button, ButtonGroup, Tooltip, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText, Popover, ToggleButtonGroup, ToggleButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LayersIcon from '@mui/icons-material/Layers';
import TuneIcon from '@mui/icons-material/Tune';
import PinIcon from '@mui/icons-material/Pin';
import SlideCanvas, { SLIDE_WIDTH, SLIDE_HEIGHT } from './SlideCanvas';
import SlidePreviewThumb from './SlidePreviewThumb';
import ElementPropertyModal from '../../modals/ElementPropertyModal';
import ObjectPropertyModal from '../../modals/ObjectPropertyModal';

// Element types that require a data source + intent to be testable
const DYNAMIC_ELEMENT_TYPES = ['table', 'chart', 'text', 'textbox'];

function setIn(obj, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const base = Array.isArray(obj) ? [...obj] : { ...obj };
  base[head] = setIn(base[head] ?? (typeof rest[0] === 'number' ? [] : {}), rest, value);
  return base;
}

/**
 * PowerPoint-style builder layout:
 * - Left sidebar: slide preview thumbnails (click to select)
 * - Main area: full slide canvas for the selected slide
 *
 * @param {Object} props
 * @param {Array} props.slides - Array of { slide_number, elements }
 * @param {number} props.selectedIndex - Index of the currently selected slide
 * @param {function} props.onSelectSlide - (index) => void
 * @param {function} props.onSlidesChange - (newSlides) => void when slide content is edited
 */
export default function PresentationBuilder({ slides = [], selectedIndex = 0, onSelectSlide, onSlidesChange, onTestPpt, testPptLoading = false, decimalPlaces: decimalPlacesProp, onDecimalPlacesChange }) {
  const safeSlides = Array.isArray(slides) ? slides : [];
  const selected = Math.min(Math.max(0, selectedIndex), Math.max(0, safeSlides.length - 1));
  const currentSlide = safeSlides[selected] ?? null;
  const currentElements = currentSlide?.elements ?? [];

  const [selectedElementIndex, setSelectedElementIndex] = React.useState(null);
  const [slideModalOpen, setSlideModalOpen] = React.useState(false);
  const [objectModalOpen, setObjectModalOpen] = React.useState(false);
  const [propertyMenuAnchor, setPropertyMenuAnchor] = React.useState(null);
  // Use controlled value from parent if provided, otherwise manage locally
  const [localDecimalPlaces, setLocalDecimalPlaces] = React.useState(2);
  const decimalPlaces = decimalPlacesProp !== undefined ? decimalPlacesProp : localDecimalPlaces;
  const setDecimalPlaces = (v) => { setLocalDecimalPlaces(v); onDecimalPlacesChange?.(v); };
  const [decimalAnchor, setDecimalAnchor] = React.useState(null);

  // Test Slide is enabled when the current slide has at least one dynamic element
  // that can be resolved — either via its own override or the slide-level default.
  const isTestEnabled = React.useMemo(() => {
    if (!currentSlide) return false;
    const dynamicEls = (currentSlide.elements ?? []).filter((el) =>
      DYNAMIC_ELEMENT_TYPES.includes(el.type)
    );
    if (dynamicEls.length === 0) return false;
    const slideConfigured = Boolean(
      currentSlide.data_source && (currentSlide.intent || currentSlide.query)
    );
    return dynamicEls.every(
      (el) => (el.data_source && (el.intent || el.query)) || slideConfigured
    );
  }, [currentSlide]);

  // Responsive main canvas: measure container and scale slide to fit
  const canvasContainerRef = React.useRef(null);
  const [canvasDims, setCanvasDims] = React.useState({ width: SLIDE_WIDTH, height: SLIDE_HEIGHT });

  React.useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const availW = entry.contentRect.width;
        if (availW <= 0) return;
        // Scale down to fit container width, but never upscale beyond native size
        const s = Math.min(availW / SLIDE_WIDTH, 1);
        setCanvasDims({
          width: Math.round(SLIDE_WIDTH * s),
          height: Math.round(SLIDE_HEIGHT * s),
        });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    setSelectedElementIndex(null);
  }, [selected]);

  const selectedElement = selectedElementIndex != null ? currentElements[selectedElementIndex] : null;
  const canEditSlide = typeof onSlidesChange === 'function' && currentSlide != null;
  const selectedDynamicElement =
    selectedElement && DYNAMIC_ELEMENT_TYPES.includes(selectedElement.type)
      ? selectedElement
      : null;

  const openPropertyMenu = React.useCallback((e) => {
    setPropertyMenuAnchor(e.currentTarget);
  }, []);

  const closePropertyMenu = React.useCallback(() => {
    setPropertyMenuAnchor(null);
  }, []);

  const saveSlideProperties = React.useCallback(
    ({ data_source, intent, query }) => {
      if (typeof onSlidesChange !== 'function') return;
      const newSlides = safeSlides.map((s, i) =>
        i !== selected ? s : { ...s, data_source, intent, query: query ?? '' }
      );
      onSlidesChange(newSlides);
      setSlideModalOpen(false);
    },
    [onSlidesChange, safeSlides, selected]
  );

  const saveObjectProperties = React.useCallback(
    ({ data_source, intent, query, query_prompt, llm_instruction, time_parameters }) => {
      if (typeof onSlidesChange !== 'function' || selectedElementIndex == null) return;
      const newSlides = safeSlides.map((s, i) => {
        if (i !== selected) return s;
        const elements = [...(s.elements ?? [])];
        const el = elements[selectedElementIndex];
        if (!el) return s;
        elements[selectedElementIndex] = { ...el, data_source, intent, query, query_prompt, llm_instruction, time_parameters };
        return { ...s, elements };
      });
      onSlidesChange(newSlides);
      setObjectModalOpen(false);
    },
    [onSlidesChange, safeSlides, selected, selectedElementIndex]
  );

  const handleElementEdit = React.useCallback(
    (elementIndex, path, value) => {
      if (typeof onSlidesChange !== 'function') return;
      const newSlides = safeSlides.map((slide, i) => {
        if (i !== selected) return slide;
        const elements = [...(slide.elements ?? [])];
        const el = elements[elementIndex];
        if (!el) return slide;
        elements[elementIndex] = setIn(el, path, value);
        return { ...slide, elements };
      });
      onSlidesChange(newSlides);
    },
    [onSlidesChange, safeSlides, selected]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', minHeight: 560 }}>
      {/* Shared top bar: SLIDES label on the left, action buttons on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: 260, flexShrink: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#475569', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Slides
          </Typography>
          {safeSlides.length > 0 && (
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.75rem' }}>
              {selected + 1} / {safeSlides.length}
            </Typography>
          )}
        </Box>

        {safeSlides.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip
              title={
                testPptLoading
                  ? 'Generating test slide…'
                  : isTestEnabled
                  ? 'Generate a test PPTX for the current slide'
                  : 'Set a data source and query/intent on this slide (or per element) to enable testing'
              }
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={testPptLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
                  disabled={!isTestEnabled || testPptLoading}
                  onClick={() => typeof onTestPpt === 'function' && onTestPpt(selected)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    backgroundColor: '#22c55e',
                    '&:hover': { backgroundColor: '#16a34a' },
                    '&.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#cbd5e1' },
                  }}
                >
                  {testPptLoading ? 'Generating…' : 'Test Slide'}
                </Button>
              </span>
            </Tooltip>
            {/* Number format button */}
            <Tooltip title="Set decimal places for numeric values" arrow>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PinIcon fontSize="small" />}
                onClick={(e) => setDecimalAnchor(e.currentTarget)}
                sx={{
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  minWidth: 0,
                  px: 1,
                  '&:hover': { borderColor: '#f59e0b', color: '#d97706', backgroundColor: 'rgba(245,158,11,0.04)' },
                }}
              >
                {'.'.padEnd(decimalPlaces + 1, '0') || '0'}
              </Button>
            </Tooltip>

            <Popover
              open={Boolean(decimalAnchor)}
              anchorEl={decimalAnchor}
              onClose={() => setDecimalAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { p: 1.5, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 1 }}>
                Decimal places
              </Typography>
              <ToggleButtonGroup
                value={decimalPlaces}
                exclusive
                size="small"
                onChange={(_, val) => { if (val !== null) { setDecimalPlaces(val); setDecimalAnchor(null); } }}
                sx={{ gap: 0.5 }}
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <ToggleButton
                    key={n}
                    value={n}
                    sx={{
                      width: 36,
                      height: 32,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      border: '1px solid #e2e8f0 !important',
                      borderRadius: '6px !important',
                      '&.Mui-selected': { backgroundColor: '#f59e0b', color: '#fff', borderColor: '#f59e0b !important' },
                    }}
                  >
                    {n}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Popover>

            <ButtonGroup variant="outlined" size="small" disabled={!canEditSlide}>
              <Button
                startIcon={<LayersIcon fontSize="small" />}
                onClick={() => setSlideModalOpen(true)}
                sx={{
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.04)' },
                  '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' },
                }}
              >
                Property
              </Button>
              <Button
                size="small"
                onClick={openPropertyMenu}
                sx={{
                  px: 0.5,
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.04)' },
                  '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' },
                }}
              >
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>

            <Menu
              anchorEl={propertyMenuAnchor}
              open={Boolean(propertyMenuAnchor)}
              onClose={closePropertyMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { closePropertyMenu(); setSlideModalOpen(true); }}>
                <ListItemIcon><LayersIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Slide Properties</ListItemText>
              </MenuItem>
              <MenuItem
                disabled={!selectedDynamicElement}
                onClick={() => { closePropertyMenu(); setObjectModalOpen(true); }}
              >
                <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
                <ListItemText>
                  Object Properties
                  {selectedDynamicElement && (
                    <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#64748b' }}>
                      ({selectedDynamicElement.type})
                    </span>
                  )}
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Panels row: thumbnails left, canvas right — both start at same Y */}
      <Box sx={{ display: 'flex', gap: 2.5, flex: 1, minWidth: 0 }}>
        {/* Left sidebar: slide previews (no header — it's in the top bar) */}
        <Paper
          elevation={0}
          sx={{
            width: 260,
            flexShrink: 0,
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '8px 16px 8px',
            backgroundColor: '#f8fafc',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 200px)',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: 3 },
          }}
        >
          {safeSlides.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8', px: 0.5, py: 4, textAlign: 'center' }}>
              No slides yet
            </Typography>
          ) : (
            safeSlides.map((slide, index) => (
              <SlidePreviewThumb
                key={`slide-${index}`}
                slide={slide}
                index={index}
                isActive={index === selected}
                onClick={onSelectSlide}
                decimalPlaces={decimalPlaces}
              />
            ))
          )}
        </Paper>

        {/* Canvas */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            backgroundColor: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            minWidth: 0,
          }}
        >
          <Box
            ref={canvasContainerRef}
            sx={{
              width: '100%',
              backgroundColor: '#fff',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <SlideCanvas
              elements={currentElements}
              width={canvasDims.width}
              height={canvasDims.height}
              isPreview={false}
              editable={typeof onSlidesChange === 'function'}
              onElementEdit={handleElementEdit}
              selectedElementIndex={selectedElementIndex}
              onSelectElement={setSelectedElementIndex}
              decimalPlaces={decimalPlaces}
            />
          </Box>
        </Paper>
      </Box>

      <ElementPropertyModal
        open={slideModalOpen}
        onClose={() => setSlideModalOpen(false)}
        slideDataSource={currentSlide?.data_source ?? ''}
        slideIntent={currentSlide?.intent ?? ''}
        slideQuery={currentSlide?.query ?? ''}
        onSave={saveSlideProperties}
      />

      <ObjectPropertyModal
        open={objectModalOpen}
        onClose={() => setObjectModalOpen(false)}
        elementType={selectedDynamicElement?.type ?? ''}
        dataSource={selectedDynamicElement?.data_source ?? ''}
        intent={selectedDynamicElement?.intent ?? ''}
        description={selectedDynamicElement?.description ?? ''}
        query={selectedDynamicElement?.query ?? ''}
        queryPrompt={selectedDynamicElement?.query_prompt ?? ''}
        llmInstruction={selectedDynamicElement?.llm_instruction ?? ''}
        timeParameters={selectedDynamicElement?.time_parameters ?? []}
        onSave={saveObjectProperties}
      />
    </Box>
  );
}
